/**
 * Redacts patient data from the URLs Datadog RUM transmits.
 *
 * `defaultPrivacyLevel: 'mask'` masks the replayed DOM; it does not touch `view.url`,
 * `view.referrer` or `resource.url`, which ride along on every RUM event. Several flows put patient
 * data in the query string — a base64 email on the password-reset link, a base64 eligibility
 * payload on the referral link — and base64 is not encryption, so it has to come off here.
 *
 * Fails closed: a URL that cannot be parsed is replaced wholesale rather than sent on uninspected.
 */

const REDACTED = 'REDACTED';

/** Query parameters that carry patient data, or a payload that embeds it. Extend as flows are added. */
const REDACTED_PARAMS = ['email', 'data', 'q', 'subject', 'message_params'];

/**
 * A path segment that identifies a person or record rather than naming a route: a uuid, a numeric
 * id, or a base64 blob. Kept deliberately narrow — over-redaction destroys the route grouping that
 * makes RUM worth having.
 */
const IDENTIFIER_SEGMENT =
	/^(?:\d{4,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[A-Za-z0-9+/=_-]{24,})$/i;

/** Replaces sensitive query values and identifier-shaped path segments with `REDACTED`. */
export function scrubUrl(url: string): string {
	if (!url) {
		return url;
	}

	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		// Not parseable, so it cannot be inspected. Drop the whole thing rather than guess.
		return REDACTED;
	}

	REDACTED_PARAMS.forEach(param => {
		if (parsed.searchParams.has(param)) {
			parsed.searchParams.set(param, REDACTED);
		}
	});

	parsed.pathname = parsed.pathname
		.split('/')
		.map(segment => (IDENTIFIER_SEGMENT.test(segment) ? REDACTED : segment))
		.join('/');

	return parsed.toString();
}

/**
 * Datadog RUM `beforeSend` hook. Scrubs every URL an outgoing event carries and always keeps the
 * event — the goal is to strip PHI, not to lose telemetry.
 *
 * Typed loosely because the SDK's RumEvent union marks these fields readonly, while `beforeSend`
 * exists precisely so they can be rewritten in place.
 */
export function scrubRumEventUrls(event: any): boolean {
	if (event?.view?.url) {
		event.view.url = scrubUrl(event.view.url);
	}
	if (event?.view?.referrer) {
		event.view.referrer = scrubUrl(event.view.referrer);
	}
	if (event?.resource?.url) {
		event.resource.url = scrubUrl(event.resource.url);
	}
	if (event?.error?.resource?.url) {
		event.error.resource.url = scrubUrl(event.error.resource.url);
	}

	return true;
}
